/**
 * Chip text editor for "Anpassa text" (ADR-0102 addendum): editable text
 * with form fields as atomic, non-editable chips. Its value is the
 * framework's token text (`{{key}}` = live reference, `\n` = line break),
 * which the adaptation session parses back into inline content.
 *
 * Rendered inside the document's shadow root, styled by `editor-css.ts`.
 * Deleting a chip disconnects that spot from the form (the caller warns);
 * "Infoga fält" inserts a chip for any field — or for a new own field
 * ("Nytt eget fält"), which the caller defines when the text is saved.
 */

import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type ClipboardEvent,
  type KeyboardEvent,
} from 'react';
import type { CustomFieldType } from '@hyranai/regta-document-ir';
import {
  CUSTOM_FIELD_TYPES,
  CUSTOM_FIELD_TYPE_LABEL,
  groupPickerFields,
  tokenSegments,
  type PendingField,
  type PickerField,
} from './adapt.js';
import { createChip, fillTokenDom, insertAt, placeCaret, rangeInside, serializeTokenDom, type ChipInfo } from './token-dom.js';

export type TokenEditorHandle = {
  insertChip(key: string, info?: ChipInfo): void;
  focus(): void;
};

export const TokenEditor = forwardRef<
  TokenEditorHandle,
  {
    initial: string;
    chip: (key: string) => ChipInfo | null;
    onChange: (text: string) => void;
    /** Runs first; a handled key (`preventDefault`) is not processed further. */
    onKeyDown?: (e: KeyboardEvent) => void;
    autoFocus?: boolean;
    placeholder?: string;
    label: string;
    minLines?: number;
  }
>(function TokenEditor({ initial, chip, onChange, onKeyDown, autoFocus, placeholder, label, minLines = 3 }, ref) {
  const rootRef = useRef<HTMLDivElement | null>(null);
  const saved = useRef<Range | null>(null);
  const [empty, setEmpty] = useState(initial === '');
  const chipRef = useRef(chip);
  chipRef.current = chip;

  const emit = () => {
    const root = rootRef.current;
    if (!root) return;
    const text = serializeTokenDom(root);
    setEmpty(text === '');
    onChange(text);
  };

  useLayoutEffect(() => {
    const root = rootRef.current;
    if (!root) return;
    const info = (key: string) => chipRef.current(key) ?? { label: key };
    fillTokenDom(root, tokenSegments(initial, (key) => chipRef.current(key) != null), info);
    if (autoFocus) {
      root.focus();
      const end = root.ownerDocument.createRange();
      end.selectNodeContents(root);
      end.collapse(false);
      placeCaret(root, end);
    }
    // The DOM is the editor's own state from here on.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const root = rootRef.current;
    if (!root) return;
    const doc = root.ownerDocument;
    const onSelection = () => {
      const r = rangeInside(root);
      if (r) saved.current = r;
    };
    doc.addEventListener('selectionchange', onSelection);
    return () => doc.removeEventListener('selectionchange', onSelection);
  }, []);

  const insert = (node: Node) => {
    const root = rootRef.current;
    if (!root) return;
    const range = rangeInside(root) ?? saved.current;
    const after = insertAt(root, range, node);
    root.focus();
    placeCaret(root, after);
    saved.current = after.cloneRange();
    emit();
  };

  useImperativeHandle(ref, () => ({
    insertChip(key, info) {
      const root = rootRef.current;
      if (!root) return;
      const shown = info ?? chipRef.current(key) ?? { label: key };
      insert(createChip(root.ownerDocument, key, shown));
    },
    focus() {
      rootRef.current?.focus();
    },
  }));

  const keyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    onKeyDown?.(e);
    if (e.defaultPrevented) return;
    if (e.key === 'Enter' && !e.metaKey && !e.ctrlKey) {
      e.preventDefault();
      insert(e.currentTarget.ownerDocument.createTextNode('\n'));
      return;
    }
    // No formatting: the value is plain token text.
    if ((e.metaKey || e.ctrlKey) && ['b', 'i', 'u'].includes(e.key.toLowerCase())) e.preventDefault();
  };

  const paste = (e: ClipboardEvent<HTMLDivElement>) => {
    e.preventDefault();
    const text = e.clipboardData.getData('text/plain').replace(/\r\n?/g, '\n');
    if (text) insert(e.currentTarget.ownerDocument.createTextNode(text));
  };

  return (
    <div className="ft-tokens-wrap">
      <div
        ref={rootRef}
        className="ft-tokens"
        role="textbox"
        aria-multiline="true"
        aria-label={label}
        contentEditable
        suppressContentEditableWarning
        spellCheck
        style={{ minHeight: `${minLines * 1.6}em` }}
        onInput={emit}
        onKeyDown={keyDown}
        onPaste={paste}
        onDrop={(e) => e.preventDefault()}
      />
      {empty && placeholder && <div className="ft-tokens-placeholder">{placeholder}</div>}
    </div>
  );
});

/**
 * "Infoga fält": a searchable list of the fields a chip may reference,
 * grouped like the form, plus "Nytt eget fält" (namn + typ).
 */
export function FieldPicker({
  fields,
  labels,
  onPick,
  onCreate,
  onClose,
}: {
  fields: readonly PickerField[];
  labels: ReadonlyMap<string, string>;
  onPick: (key: string) => void;
  /** Returns the new field, or a Swedish error. */
  onCreate?: (label: string, type: CustomFieldType) => PendingField | string;
  onClose: () => void;
}) {
  const [query, setQuery] = useState('');
  const [newLabel, setNewLabel] = useState('');
  const [newType, setNewType] = useState<CustomFieldType>('text');
  const [error, setError] = useState<string | null>(null);
  const groups = useMemo(() => groupPickerFields(fields, query), [fields, query]);

  const create = () => {
    if (!onCreate) return;
    const label = newLabel.replace(/\s+/g, ' ').trim();
    if (!label) {
      setError('Ange ett namn på fältet.');
      return;
    }
    const out = onCreate(label, newType);
    if (typeof out === 'string') setError(out);
  };
  const escape = (e: KeyboardEvent) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      e.stopPropagation();
      onClose();
    }
  };

  return (
    <div className="ft-picker" role="dialog" aria-label="Infoga fält" onKeyDown={escape}>
      <input
        autoFocus
        className="ft-picker__search"
        placeholder="Sök fält…"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
      />
      <div className="ft-picker__list">
        {groups.length === 0 && <p className="ft-picker__empty">Inga fält matchar.</p>}
        {groups.map((g) => (
          <div key={g.group}>
            <div className="ft-picker__group">{g.group}</div>
            {g.fields.map((f) => (
              <button
                key={f.key}
                type="button"
                className="ft-picker__item"
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => onPick(f.key)}
              >
                <span className="ft-picker__label">{labels.get(f.key) ?? f.label}</span>
                <span className="ft-picker__preview">{f.pending ? 'nytt fält' : (f.preview ?? 'tomt')}</span>
              </button>
            ))}
          </div>
        ))}
      </div>
      {onCreate && (
        <div className="ft-picker__new">
          <div className="ft-picker__group">Nytt eget fält</div>
          <div className="ft-picker__row">
            <input
              placeholder="Namn, t.ex. Besiktningsdatum"
              value={newLabel}
              onChange={(e) => {
                setNewLabel(e.target.value);
                setError(null);
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  create();
                }
              }}
            />
            <select value={newType} onChange={(e) => setNewType(e.target.value as CustomFieldType)} aria-label="Typ">
              {CUSTOM_FIELD_TYPES.map((t) => (
                <option key={t} value={t}>
                  {CUSTOM_FIELD_TYPE_LABEL[t]}
                </option>
              ))}
            </select>
          </div>
          {error && <p className="ft-note ft-note--warn">{error}</p>}
          <p className="ft-picker__hint">Fältet hamnar under ”Egna uppgifter” i formuläret och fylls i där.</p>
          <div className="ft-editor-actions">
            <button type="button" className="ft-btn" onClick={onClose}>
              Stäng
            </button>
            <button type="button" className="ft-btn ft-btn--primary" onClick={create}>
              Skapa och infoga
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
